<template>
  <div id="title" class="sticky top-0 z-10 mt-12 bg-white pb-3">
    <div class="pb-3 pt-5 font-sans font-medium text-theme-1">
      {{ $t("pages.sell-a-profile.gallery.title", { bundle_name: bundleName }) }}
    </div>

    <div class="flex items-center justify-between rounded bg-neutral-100 md:max-w-[345px]">
      <button
        v-for="type in galleryTypes"
        :key="`button-${type}`"
        class="m-1 grow rounded py-2 font-normal leading-5 hover:bg-stone-200 active:bg-[#DADADA]"
        :class="{ selected: galleryToShow === type }"
        :data-testid="type === 'EDITED' ? 'after-edit' : 'before-edit'"
        @click="galleryToShow = type"
      >
        <span>{{ $t(galleryLabels[type]) }}</span>
      </button>
    </div>
  </div>

  <div v-for="type in galleryTypes" :key="`galley-${type}`" :data-testid="`${type.toLowerCase()}-gallery`">
    <MasonryWall
      :class="{ hide: galleryToShow !== type }"
      :items="currentGallery[type]"
      :minColumns="1"
      :maxColumns="5"
      :columnWidth="300"
      :gap="8"
    >
      <template #default="{ item, index }">
        <GalleryItem
          :item="item"
          :type="type"
          :opposite-item="getOppositeItem(type, index)"
          @isExpandedImage="expandImageHandler"
        />
      </template>
    </MasonryWall>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import MasonryWall from "@yeger/vue-masonry-wall";
import GalleryItem from "@/features/sell-a-profile/components/preview/GalleryItem.vue";
import { PreviewPhotoItem } from "@/features/sell-a-profile/components/preview/UserPreviewGallery.vue";

const galleryTypes = ["EDITED", "ORIGINAL"] as const;
export type GalleryType = (typeof galleryTypes)[number];

const props = defineProps<{
  bundleName: string;
  gallery: string[];
  originalGallery: string[];
}>();

const galleryToShow = ref<GalleryType>("EDITED");
const preventKeyboardShortcut = ref(false);

const galleryLabels: Record<GalleryType, string> = {
  EDITED: "pages.sell-a-profile.preview-gallery.edited",
  ORIGINAL: "pages.sell-a-profile.preview-gallery.originals",
};

const getOppositeItem = (type: GalleryType, index: number) =>
  currentGallery.value[type === "EDITED" ? "ORIGINAL" : "EDITED"][index];

const formatItem = (photoUrl: string, index: number): PreviewPhotoItem => ({
  status: "SUCCESS",
  fileId: String(index),
  fileName: photoUrl,
  presignedUrl: photoUrl,
});

const currentGallery = computed(() => ({
  EDITED: props.gallery.map(formatItem),
  ORIGINAL: props.originalGallery.map(formatItem),
}));

const expandImageHandler = (isExpandedImage: boolean) => {
  preventKeyboardShortcut.value = isExpandedImage;
};

const handleKeydown = (e: KeyboardEvent) => {
  if (preventKeyboardShortcut.value) return;
  if (e.key === "ArrowLeft") galleryToShow.value = "EDITED";
  if (e.key === "ArrowRight") galleryToShow.value = "ORIGINAL";
};

onMounted(() => document.addEventListener("keydown", handleKeydown));
onUnmounted(() => document.removeEventListener("keydown", handleKeydown));
</script>

<style scoped lang="scss">
.hide {
  @apply h-0 overflow-hidden opacity-0;
}

button {
  &.selected {
    @apply bg-white shadow;
  }
}
</style>
